/**
 * CDN entry point for ICPaaS AI SDK
 * 
 * Provides a global window.ICPaasAI object for browser usage via script tag.
 * 
 * Usage:
 * ```html
 * <script src="https://cdn.icpaas.com/sdk.min.js"></script>
 * <script>
 *   // A web page always uses the publishable pk_ key, never sk_.
 *   ICPaasAI.init({ apiKey: 'pk_...' }).then(ai => {
 *     ai.createTextWidget({ agentId: 'your-agent-id' }); // floating chat
 *   });
 * </script>
 * ```
 */

import { ICPaasAI as ICPaasAIClient } from './client';
import type { ICPaasConfig } from './types';

// Version of the SDK
const VERSION = '1.0.0';

/**
 * Global ICPaasAI namespace for CDN usage
 */
const ICPaasAI = {
  /**
   * SDK version
   */
  version: VERSION,

  /**
   * Initialize and authenticate the SDK in one step (convenience method)
   * 
   * @example
   * ```javascript
   * ICPaasAI.init({
   *   apiKey: 'pk_your_publishable_key',
   *   baseURL: 'https://your-instance.com'
   * }).then(ai => {
   *   // SDK is ready and authenticated
   *   ai.startVoiceCall({ persona: 'IND_Male' });
   * });
   * ```
   */
  async init(config: ICPaasConfig): Promise<ICPaasAIClient> {
    const client = new ICPaasAIClient(config);
    await client.login();
    return client;
  },

  /**
   * Create a new SDK instance without auto-authentication
   * Use this if you want to control when login() is called
   * 
   * @example
   * ```javascript
   * const ai = ICPaasAI.create({
   *   apiKey: 'pk_your_publishable_key',
   *   baseURL: 'https://your-instance.com'
   * });
   * 
   * // Later, when you're ready
   * await ai.login();
   * await ai.startVoiceCall({ persona: 'IND_Male' });
   * ```
   */
  create(config: ICPaasConfig): ICPaasAIClient {
    return new ICPaasAIClient(config);
  },

  /**
   * Direct access to the ICPaasAI class
   * For advanced users who want to use the class directly
   */
  ICPaasAI: ICPaasAIClient,
};

// Attach to window object for global access
if (typeof window !== 'undefined') {
  (window as any).ICPaasAI = ICPaasAI;
}

// Export for module systems (in case someone imports the CDN bundle)
export default ICPaasAI;
